import { AlertTriangle, Info } from 'lucide-react';
import type { Auswirkung } from '@/api/plans.api';

// ─── Was der Tarifwechsel bricht ─────────────────────────────────────────────
//
// Steht im Dialog, bevor gespeichert wird. Bewusst kein Verbot und keine
// Rückfrage: manchmal ist ein Herunterstufen genau das, was gewollt ist. Der
// Betreiber soll es nur nicht aus Versehen tun.
//
// Zwei Gewichte, zwei Farben. „hart" heißt: der Kunde steht danach über der
// Grenze und kann nichts mehr hinzufügen. „weich" heißt: etwas verschwindet
// aus der Oberfläche, die Daten bleiben. Beides ist kein Datenverlust – und
// die Texte sagen das auch, damit niemand aus Vorsicht das Falsche tut.

interface Props {
  auswirkung: Auswirkung | undefined;
  laeuft: boolean;
  /** Bei einer Tarifänderung: „für 12 Konten". Bei einem Konto: nichts. */
  mehrereKonten?: boolean;
}

export function Tarifwirkung({ auswirkung, laeuft, mehrereKonten = false }: Props) {
  if (laeuft) {
    return (
      <p className="mt-3 text-xs text-slate-400">Auswirkung wird geprüft…</p>
    );
  }

  if (!auswirkung || auswirkung.befunde.length === 0) return null;

  const hart = auswirkung.befunde.filter((b) => b.gewicht === 'hart');
  const weich = auswirkung.befunde.filter((b) => b.gewicht === 'weich');

  return (
    <div
      className={`mt-3 rounded-lg border p-3 ${
        hart.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <p
        className={`flex items-center gap-2 text-sm font-medium ${
          hart.length > 0 ? 'text-amber-900' : 'text-slate-700'
        }`}
      >
        {hart.length > 0 ? (
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        {mehrereKonten
          ? `Betrifft ${auswirkung.konten} ${auswirkung.konten === 1 ? 'Konto' : 'Konten'}`
          : 'Das ändert etwas für dieses Konto'}
      </p>

      <ul className="mt-2 space-y-1.5">
        {[...hart, ...weich].map((b, i) => (
          <li
            key={i}
            className={`text-xs leading-relaxed ${
              b.gewicht === 'hart' ? 'text-amber-900' : 'text-slate-600'
            }`}
          >
            {b.text}
          </li>
        ))}
      </ul>

      <p className="mt-2 border-t border-current/10 pt-2 text-xs text-slate-500">
        Nichts davon löscht Daten. Der Bestand bleibt les-, bearbeit- und
        exportierbar – nur Hinzufügen ist über der Grenze nicht mehr möglich.
      </p>
    </div>
  );
}
